/**
 * Route sync between the Vue dashboard route and the React dashboard iframe.
 *
 * React reports its own route with SYNC_ROUTE and the host replaces the Vue
 * route to match. The host must not echo that replace back as another
 * SYNC_ROUTE (React/Vue would loop), but a later navigation to a dashboard URL
 * — browser BACK/FORWARD included (TASK-13798) — must still be synced. Instead
 * of a flag and a timer around the replace, compare the route with what React
 * last reported: a route that already matches React's state needs no sync.
 */

/** Query keys the host adds on top of what React reported. */
const HOST_QUERY_KEYS = ['accountId', 'userAccountId', 'update'];

/**
 * The part of a dashboard route React is the source of truth for. Applied to
 * both sides of the comparison: React's own URL carries the host keys too
 * (it got them from an earlier SYNC_ROUTE), and comparing them would make the
 * host's replace look like a new route and echo forever.
 */
export const reactRouteOf = ({ viewId, params, query }) => ({
    viewId: String(viewId ?? params?.id ?? ''),
    query: R.omit(HOST_QUERY_KEYS, query || {}),
});

/**
 * True when `route` is the dashboard route React last reported
 * (`reported` as stored from its SYNC_ROUTE: `{ viewId, query }`).
 */
export const isReportedReactRoute = (route, reported) =>
    !!reported && R.equals(reactRouteOf(route), reactRouteOf(reported));
